import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { Close, Filter } from '@withjoy/joykit/icons';

import { useTranslation } from '@shared/core';
import { CarSearchBar } from '../CarSearchBar/CarSearchBar';
import { parseYmdToDate } from '../../CarRentalConsole.utils';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCarRentalCurrency } from '../../hooks/useCarRentalCurrency';
import { CarConsoleJoyLogo } from '@apps/stays/components/CarConsoleJoyLogo';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { CarSearchState, CarSearchTerms } from '../../CarRentalConsole.types';
import { TravelCurrencySelector } from '@shared/components/TravelCurrencySelector';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';

type CarSearchFormRowProps = Readonly<{
  isGeocoding?: boolean;
  state: CarSearchState;
  pendingDestination?: string;
  onSearchSubmit: (value: CarSearchTerms) => void;
}>;

type CarSearchHeaderProps = Readonly<{
  eventHandle: string;
  state: CarSearchState;
  onSearchOpen: () => void;
  pendingDestination?: string;
  onFilterPanelOpen: () => void;
}>;

type CarMobileSearchProps = Readonly<{
  onClose: () => void;
  isGeocoding?: boolean;
  state: CarSearchState;
  pendingDestination?: string;
  onSearchSubmit: (value: CarSearchTerms) => void;
}>;

const CarSearchHeaderInner = ({ state, eventHandle, onFilterPanelOpen, onSearchOpen, pendingDestination }: CarSearchHeaderProps) => {
  const [isCurrencySelectorEnabled] = useIsFeatureEnabled('travelGuestCurrencySwitch');
  const { restrictedCurrencyCodes, loading: isCurrencyLoading } = useCarRentalCurrency();

  const isMobileOrTablet = useIsMobileOrTablet();
  const { tq } = useTranslation('carRentalConsole');
  const { formatDateRange } = useEventLocaleFormatter();
  const dateRange = formatDateRange(parseYmdToDate(state.pickUpDate), parseYmdToDate(state.dropOffDate));

  return (
    <Flex
      top={0}
      padding={4}
      zIndex="dropdown"
      position="sticky"
      background="white"
      alignItems="center"
      borderBottom="1px solid"
      borderColor="mono3"
      justifyContent="space-between"
    >
      <CarConsoleJoyLogo eventHandle={eventHandle} />
      {isMobileOrTablet && (
        <Flex
          flex={1}
          as="button"
          paddingX={7}
          paddingY={3}
          minWidth={0}
          type="button"
          marginRight={6}
          cursor="pointer"
          background="white"
          border="1px solid"
          borderColor="mono3"
          borderRadius="full"
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          onClick={onSearchOpen}
          minHeight={pxToRem(64)}
          aria-label={tq`Edit car search`}
        >
          <TextV2
            maxWidth="100%"
            fontWeight={400}
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            typographyVariant="label2"
          >
            {state.pickUpLocation?.displayName || pendingDestination || tq`Search cars`}
          </TextV2>
          <TextV2
            marginTop={2}
            color="mono10"
            typographyVariant="label1"
          >
            {dateRange}
          </TextV2>
        </Flex>
      )}
      <Flex
        gap={6}
        alignItems="center"
      >
        {isMobileOrTablet && (
          <Filter
            size={22}
            cursor="pointer"
            aria-label={tq`Filters`}
            onClick={onFilterPanelOpen}
          />
        )}
        <TravelCurrencySelector
          restrictToCurrencyCodes={restrictedCurrencyCodes}
          isEnabled={isCurrencySelectorEnabled && !isCurrencyLoading}
        />
      </Flex>
    </Flex>
  );
};

const CarSearchHeader = Object.assign(React.memo(CarSearchHeaderInner), {
  Skeleton: () => (
    <Flex
      padding={4}
      background="white"
      minHeight={pxToRem(72)}
      borderBottom="1px solid"
      borderColor="mono3"
    />
  ),
});

const CarSearchFormRow = ({ state, onSearchSubmit, pendingDestination, isGeocoding }: CarSearchFormRowProps) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  if (isMobileOrTablet) {
    return null;
  }

  return (
    <Box
      padding={4}
      background="white"
    >
      <CarSearchBar
        state={state}
        isGeocoding={isGeocoding}
        onSubmit={onSearchSubmit}
        pendingDestination={pendingDestination}
      />
    </Box>
  );
};

const CarMobileSearch = ({ state, onClose, onSearchSubmit, pendingDestination, isGeocoding }: CarMobileSearchProps) => {
  const { tq } = useTranslation('carRentalConsole');

  return (
    <Flex
      height="97%"
      flexDirection="column"
    >
      <Flex
        paddingX={6}
        paddingTop={6}
        flexShrink={0}
        justifyContent="space-between"
      >
        <TextV2 typographyVariant="hed3">{tq`Search cars`}</TextV2>
        <Close
          cursor="pointer"
          onClick={onClose}
          aria-label={tq`Close search`}
        />
      </Flex>
      <Box
        flex={1}
        width="100%"
        minHeight={0}
        overflowY="auto"
      >
        <CarSearchBar
          state={state}
          layout="mobileSheet"
          isGeocoding={isGeocoding}
          onSubmit={value => {
            onClose();
            onSearchSubmit(value);
          }}
          pendingDestination={pendingDestination}
        />
      </Box>
    </Flex>
  );
};

export { CarMobileSearch, CarSearchFormRow, CarSearchHeader };
